/**
 * Editorial map style for the Dash GSM theme. Used by both ZoneMap (full-page
 * zone editor) and MapPreview (hub-form pin preview) so the two surfaces
 * read as the same product.
 *
 * Design intent: a near-monochrome canvas where the brand-purple zone hexes
 * and the hub pin carry all the color. No consumer-map clutter — every POI,
 * transit icon, business label, and street-level label is hidden. Roads
 * reduce to hairlines. Water is a soft cool gray. Labels are kept only for
 * orientation (city, neighborhood) in ink.
 *
 * Apply via the `styles` option on `new google.maps.Map(...)`:
 *
 *   new maps.Map(el, { styles: GSM_MAP_STYLE, ... })
 *
 * Note: Google's typed `MapTypeStyle[]` is in @types/google.maps which we
 * don't have installed. Type as `any[]` here — the runtime SDK accepts the
 * shape directly.
 */
export const GSM_MAP_STYLE: any[] = [
  // ─── Hide every POI and its labels ────────────────────────────────
  // Restaurants, gas stations, banks, stores, schools, hospitals,
  // government buildings — all gone. The single biggest source of noise
  // on the default style.
  {
    featureType: 'poi',
    stylers: [{ visibility: 'off' }],
  },

  // ─── Hide all transit (bus stops, train stations, lines) ──────────
  {
    featureType: 'transit',
    stylers: [{ visibility: 'off' }],
  },

  // ─── Background landscape: warm off-white canvas ──────────────────
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [{ color: '#fafafa' }],
  },
  {
    featureType: 'landscape.man_made',
    elementType: 'geometry',
    stylers: [{ color: '#f4f4f5' }],
  },
  {
    featureType: 'landscape.natural',
    elementType: 'geometry',
    stylers: [{ color: '#f5f5f4' }],
  },

  // ─── Water: muted gray-blue, well below brand purple in saturation ─
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#e2e8f0' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#94a3b8' }],
  },
  {
    featureType: 'water',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#fafafa' }],
  },

  // ─── Roads: hairlines, no icons, only essential labels ────────────
  {
    featureType: 'road',
    elementType: 'labels.icon',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.fill',
    stylers: [{ color: '#ffffff' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#e5e7eb' }],
  },
  // Highways: slightly more visible since they're orientation aids.
  {
    featureType: 'road.highway',
    elementType: 'geometry.fill',
    stylers: [{ color: '#ffffff' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#d4d4d8' }],
  },
  // Hide local-street labels entirely — pure noise at this zoom.
  {
    featureType: 'road.local',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }],
  },
  // Arterial labels only — used as wayfinding for ops.
  {
    featureType: 'road.arterial',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#9ca3af' }],
  },
  {
    featureType: 'road.arterial',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#fafafa' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#6b7280' }],
  },
  {
    featureType: 'road.highway',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#fafafa' }],
  },

  // ─── Administrative geometry: subtle district outlines ────────────
  // Faint hairline so ops can see where Kelapa Gading ends and
  // Sunter begins. Strong enough to read; weak enough to never compete
  // with zone polygons.
  {
    featureType: 'administrative',
    elementType: 'geometry.fill',
    stylers: [{ visibility: 'off' }],
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#d4d4d8' }, { weight: 0.5 }],
  },
  {
    featureType: 'administrative.locality',
    elementType: 'geometry.stroke',
    stylers: [{ color: '#a1a1aa' }, { weight: 0.5 }],
  },

  // ─── Administrative labels: ink for cities, neutral for the rest ──
  {
    featureType: 'administrative.country',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#374151' }],
  },
  {
    featureType: 'administrative.locality',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#171717' }],
  },
  {
    featureType: 'administrative.neighborhood',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#4b5563' }],
  },
  {
    featureType: 'administrative',
    elementType: 'labels.text.stroke',
    stylers: [{ color: '#fafafa' }, { weight: 2 }],
  },
];
